import React, { useState, useMemo, useEffect } from 'react';
import { Upload, Download, AlertTriangle, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toCanonical, normalizeUnitKey } from '../lib/units';
import { createPrep } from '../lib/api';
import logger from '../lib/logger';
import { useDirtyExitGuard } from '../lib/useDirtyExitGuard';

/**
 * PrepUploadDialog — bulk import preps from CSV / XLSX.
 *
 * Pipeline:
 *   1. User uploads file → parse with SheetJS (handles both .csv and .xlsx).
 *   2. Show preview + column mapping (auto-detect with manual override).
 *   3. Validate every row (unit recognized, ingredient resolves, qty > 0, etc).
 *   4. Group rows by prep_name → 1 prep per name with N recipe lines.
 *   5. Show summary: preps_detected, rows_parsed, unresolved, duplicates, invalid_units.
 *   6. Operator clicks "Import" → POST /api/preps for each unique prep.
 *
 * Required columns (mappable):
 *   prep_name | expected_yield | yield_unit | ingredient_name | quantity | unit
 *   (optional: ingredient_id, wastage_percent, notes)
 */

const TEMPLATE_HEADERS = [
  'prep_name', 'expected_yield', 'yield_unit',
  'ingredient_name', 'ingredient_id', 'quantity', 'unit',
  'wastage_percent', 'notes',
];
const TEMPLATE_SAMPLE = [
  ['Tomato Sauce', 5, 'kg', 'Tomato (canonical)', '', 4, 'kg', 0, 'Slow simmer 45 min'],
  ['Tomato Sauce', 5, 'kg', 'Garlic',             '', 100, 'gm', 0, ''],
  ['Tomato Sauce', 5, 'kg', 'Olive Oil',          '', 200, 'ml', 0, ''],
  ['Garlic Paste', 1, 'kg', 'Garlic',             '', 1, 'kg', 5, 'Peel before weighing'],
];

const REQUIRED_FIELDS = ['prep_name', 'expected_yield', 'yield_unit', 'quantity', 'unit'];
const RESOLVABLE = ['ingredient_id', 'ingredient_name'];  // need at least one

// Optional column appended on Export so re-imports can detect stale overwrites.
// Backward-compatible: if the file doesn't carry it, the conflict guard is
// silently skipped.
const OPTIONAL_HEADERS = ['last_updated_at'];

const guessMapping = (headers) => {
  const out = {};
  const norm = (s) => (s || '').toString().toLowerCase().replace(/[\s_-]/g, '');
  [...TEMPLATE_HEADERS, ...OPTIONAL_HEADERS].forEach((target) => {
    const t = norm(target);
    const found = headers.find((h) => norm(h) === t);
    if (found) out[target] = found;
  });
  return out;
};

const PrepUploadDialog = ({ open, onClose, ingredients = [], existingPreps = [], onImported }) => {
  const [step, setStep] = useState('upload'); // 'upload' | 'mapping' | 'preview' | 'importing'
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]); // array of objects keyed by raw headers
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const ingredientsByName = useMemo(() => {
    const m = new Map();
    ingredients.forEach((i) => {
      const n = (i.ingredient_name || i.name || '').trim().toLowerCase();
      if (n && !m.has(n)) m.set(n, i);
    });
    return m;
  }, [ingredients]);
  const ingredientsById = useMemo(() => {
    const m = new Map();
    ingredients.forEach((i) => m.set(i.ingredient_id, i));
    return m;
  }, [ingredients]);

  // Map prep_name (lower) → updated_at. Used to detect when a re-imported
  // exported prep has gone stale. Backward-compatible: only fires when the
  // file carries `last_updated_at`.
  const existingUpdatedAtByName = useMemo(() => {
    const m = new Map();
    (existingPreps || []).forEach((p) => {
      const n = (p.prep_name || '').trim().toLowerCase();
      if (n) m.set(n, p.updated_at || p.created_at || '');
    });
    return m;
  }, [existingPreps]);

  const [conflictsAcknowledged, setConflictsAcknowledged] = useState(false);
  useEffect(() => { setConflictsAcknowledged(false); }, [rawRows, mapping]);

  const reset = () => {
    setStep('upload');
    setRawHeaders([]);
    setRawRows([]);
    setMapping({});
    setImporting(false);
    setImportResults(null);
  };

  const closeAndReset = () => {
    reset();
    onClose?.();
  };

  const isDirty = useMemo(() => {
    if (importResults) return false;
    if (step !== 'upload') return true;
    if (rawHeaders.length > 0 || rawRows.length > 0) return true;
    if (Object.keys(mapping).length > 0) return true;
    return false;
  }, [step, rawHeaders, rawRows, mapping, importResults]);

  const exitGuard = useDirtyExitGuard({
    isDirty,
    onConfirmClose: closeAndReset,
    title: 'Exit prep upload?',
    body: 'You have unsaved prep upload progress. If you exit now, your uploaded file and column mapping will be lost.',
    testIdPrefix: 'prep-upload-exit-confirm',
  });

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
      if (!json.length) {
        toast.error('File is empty');
        return;
      }
      const headers = Object.keys(json[0]);
      setRawHeaders(headers);
      setRawRows(json);
      setMapping(guessMapping(headers));
      setStep('mapping');
    } catch (e) {
      logger.error('upload parse failed', e);
      toast.error('Could not parse file. Please use CSV or XLSX format.');
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Preps');
    XLSX.writeFile(wb, 'prep_import_template.xlsx');
  };

  const downloadCsvTemplate = () => {
    const csv = [TEMPLATE_HEADERS.join(','), ...TEMPLATE_SAMPLE.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'prep_import_template.csv';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // Validate + group rows once mapping is set
  const validation = useMemo(() => {
    if (!rawRows.length) return null;
    const get = (row, target) => {
      const src = mapping[target];
      return src ? row[src] : '';
    };

    const groups = new Map();   // prep_name → { prep_name, expected_yield, yield_unit, lines: [] }
    const issues = [];          // [{rowIndex, type, message}]
    const unresolvedNames = new Set();
    const invalidUnits = new Set();
    const staleConflicts = new Set(); // prep names whose exported timestamp differs from DB

    rawRows.forEach((row, rIdx) => {
      const name = (get(row, 'prep_name') || '').toString().trim();
      const yieldRaw = get(row, 'expected_yield');
      const yieldUnit = (get(row, 'yield_unit') || '').toString().trim();
      const ingId = (get(row, 'ingredient_id') || '').toString().trim();
      const ingName = (get(row, 'ingredient_name') || '').toString().trim();
      const qtyRaw = get(row, 'quantity');
      const unit = (get(row, 'unit') || '').toString().trim();
      const fileLastUpdated = (get(row, 'last_updated_at') || '').toString().trim();

      // Required-field gate
      if (!name) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'prep_name missing' }); return; }
      if (!yieldRaw) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'expected_yield missing' }); return; }
      if (!yieldUnit) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'yield_unit missing' }); return; }
      if (!qtyRaw) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'quantity missing' }); return; }
      if (!unit) { issues.push({ rowIndex: rIdx, type: 'missing_field', message: 'unit missing' }); return; }

      const expected_yield = Number(yieldRaw);
      const qty = Number(qtyRaw);
      if (!isFinite(expected_yield) || expected_yield <= 0) {
        issues.push({ rowIndex: rIdx, type: 'invalid_qty', message: `bad expected_yield: ${yieldRaw}` });
        return;
      }
      if (!isFinite(qty) || qty <= 0) {
        issues.push({ rowIndex: rIdx, type: 'invalid_qty', message: `bad quantity: ${qtyRaw}` });
        return;
      }

      // Unit recognition (we accept any recognized unit; will canonicalize on import)
      if (toCanonical(1, yieldUnit).conversion_failed) {
        invalidUnits.add(yieldUnit);
        issues.push({ rowIndex: rIdx, type: 'invalid_unit', message: `yield_unit '${yieldUnit}' not recognized` });
        return;
      }
      if (toCanonical(1, unit).conversion_failed) {
        invalidUnits.add(unit);
        issues.push({ rowIndex: rIdx, type: 'invalid_unit', message: `unit '${unit}' not recognized` });
        return;
      }

      // Ingredient resolution: prefer ingredient_id, fall back to ingredient_name
      let resolved = null;
      if (ingId) resolved = ingredientsById.get(ingId);
      if (!resolved && ingName) resolved = ingredientsByName.get(ingName.toLowerCase());
      if (!resolved) {
        unresolvedNames.add(ingName || `(row ${rIdx + 1})`);
        issues.push({ rowIndex: rIdx, type: 'unresolved', message: `cannot resolve "${ingName || ingId}"` });
        return;
      }

      // Group
      if (!groups.has(name)) {
        groups.set(name, { prep_name: name, expected_yield, yield_unit: yieldUnit, lines: [] });
        // Stale-row guard (only if export-time timestamp is present in the row).
        if (fileLastUpdated) {
          const dbStamp = existingUpdatedAtByName.get(name.toLowerCase()) || '';
          if (dbStamp && dbStamp !== fileLastUpdated) {
            staleConflicts.add(name);
          }
        }
      } else {
        // sanity: same prep should have same yield/unit (operator misuse)
        const g = groups.get(name);
        if (g.expected_yield !== expected_yield || g.yield_unit !== yieldUnit) {
          issues.push({ rowIndex: rIdx, type: 'inconsistent', message: `prep "${name}" has conflicting expected_yield or yield_unit across rows` });
          return;
        }
      }
      groups.get(name).lines.push({
        ingredient_id: resolved.ingredient_id,
        ingredient_name: resolved.ingredient_name || resolved.name,
        qty,
        base_unit: unit,
      });
    });

    return {
      preps: Array.from(groups.values()),
      issues,
      unresolvedNames: Array.from(unresolvedNames),
      invalidUnits: Array.from(invalidUnits),
      staleConflicts: Array.from(staleConflicts),
    };
  }, [rawRows, mapping, ingredientsByName, ingredientsById, existingUpdatedAtByName]);

  const hasUnacknowledgedStale =
    !!validation && validation.staleConflicts.length > 0 && !conflictsAcknowledged;
  const canProceed = validation && validation.preps.length > 0 && validation.issues.length === 0 && !hasUnacknowledgedStale;
  const canImportAnyway = validation && validation.preps.length > 0 && !hasUnacknowledgedStale;

  const runImport = async () => {
    if (!validation || !validation.preps.length) return;
    setImporting(true);
    setStep('importing');
    const out = { created: [], failed: [] };
    for (const p of validation.preps) {
      try {
        await createPrep({
          prep_name: p.prep_name,
          expected_yield: p.expected_yield,
          base_unit: normalizeUnitKey(p.yield_unit),
          recipe: p.lines.map((l) => ({
            ingredient_id: l.ingredient_id,
            qty: l.qty,
            base_unit: normalizeUnitKey(l.base_unit),
          })),
        });
        out.created.push(p.prep_name);
      } catch (e) {
        out.failed.push({ name: p.prep_name, error: e?.response?.data?.detail || e.message });
        logger.error('prep import failed', p.prep_name, e);
      }
    }
    setImporting(false);
    setImportResults(out);
    if (out.failed.length === 0) {
      toast.success(`Imported ${out.created.length} prep(s)`);
      onImported?.();
    } else {
      toast.warning(`Imported ${out.created.length}, failed ${out.failed.length}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) exitGuard.requestClose(); }}>
      <DialogContent
        className="bg-[#18181b] border-[#27272a] text-white max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="prep-upload-dialog"
        onEscapeKeyDown={exitGuard.onEscapeKeyDown}
        onPointerDownOutside={exitGuard.onPointerDownOutside}
        onInteractOutside={exitGuard.onInteractOutside}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Bulk Import Preps</DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-400">Upload a CSV or XLSX file. Multiple rows with the same <code>prep_name</code> are grouped into one prep with multiple recipe lines.</p>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={downloadTemplate}
                className="flex items-center gap-2 px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm"
                data-testid="download-xlsx-template-btn"
              ><Download className="w-4 h-4" /> Download .xlsx template</button>
              <button
                onClick={downloadCsvTemplate}
                className="flex items-center gap-2 px-3 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md text-sm"
                data-testid="download-csv-template-btn"
              ><Download className="w-4 h-4" /> Download .csv template</button>
            </div>
            <label className="block border-2 border-dashed border-[#3f3f46] rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500/50 transition-colors" data-testid="prep-upload-dropzone">
              <Upload className="w-10 h-10 mx-auto mb-2 text-zinc-500" />
              <p className="text-sm text-zinc-300">Click to choose file or drop here</p>
              <p className="text-xs text-zinc-500 mt-1">Accepts .csv, .xlsx, .xls</p>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="prep-upload-file-input"
              />
            </label>
          </div>
        )}

        {step === 'mapping' && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-400">Map your columns to expected fields. Auto-detected mappings shown.</p>
            <div className="grid grid-cols-2 gap-3" data-testid="mapping-grid">
              {TEMPLATE_HEADERS.map((target) => {
                const required = REQUIRED_FIELDS.includes(target);
                const ok = !!mapping[target];
                return (
                  <div key={target}>
                    <label className="block text-xs text-zinc-400 mb-1">
                      {target}{required && <span className="text-red-400 ml-1">*</span>}
                    </label>
                    <select
                      value={mapping[target] || ''}
                      onChange={(e) => setMapping((m) => ({ ...m, [target]: e.target.value }))}
                      className={`w-full px-2 py-2 bg-[#09090b] border rounded-md text-sm ${ok ? 'border-emerald-500/40' : 'border-[#3f3f46]'}`}
                      data-testid={`mapping-select-${target}`}
                    >
                      <option value="">— skip —</option>
                      {rawHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-zinc-500">
              At least one of <code>ingredient_id</code> or <code>ingredient_name</code> is required to resolve ingredients.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setStep('upload')} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm" data-testid="mapping-back-btn">Back</button>
              <button
                onClick={() => setStep('preview')}
                disabled={!REQUIRED_FIELDS.every((f) => mapping[f]) || !RESOLVABLE.some((f) => mapping[f])}
                className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold disabled:opacity-40"
                data-testid="mapping-next-btn"
              >Continue →</button>
            </div>
          </div>
        )}

        {step === 'preview' && validation && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center" data-testid="preview-summary">
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Preps detected</p>
                <p className="text-xl font-mono font-bold text-emerald-500" data-testid="preview-preps-count">{validation.preps.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Rows parsed</p>
                <p className="text-xl font-mono font-bold">{rawRows.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Unresolved</p>
                <p className={`text-xl font-mono font-bold ${validation.unresolvedNames.length ? 'text-yellow-400' : 'text-zinc-300'}`} data-testid="preview-unresolved-count">{validation.unresolvedNames.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Invalid units</p>
                <p className={`text-xl font-mono font-bold ${validation.invalidUnits.length ? 'text-yellow-400' : 'text-zinc-300'}`}>{validation.invalidUnits.length}</p>
              </div>
              <div className="p-2 bg-[#09090b] border border-[#27272a] rounded">
                <p className="text-xs text-zinc-500">Issues</p>
                <p className={`text-xl font-mono font-bold ${validation.issues.length ? 'text-red-400' : 'text-emerald-500'}`}>{validation.issues.length}</p>
              </div>
            </div>

            {validation.staleConflicts.length > 0 && (
              <div
                className="bg-amber-500/5 border border-amber-500/30 rounded-md p-3 space-y-2"
                data-testid="prep-preview-stale-callout"
              >
                <p className="text-sm font-bold text-amber-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {validation.staleConflicts.length} prep(s) modified since you exported them
                </p>
                <p className="text-xs text-zinc-300">{validation.staleConflicts.join(', ')}</p>
                <p className="text-xs text-amber-300/80">
                  Re-importing may overwrite changes made after your export. Tick the box below to acknowledge and proceed.
                </p>
                <label className="flex items-center gap-2 text-xs text-amber-200 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={conflictsAcknowledged}
                    onChange={(e) => setConflictsAcknowledged(e.target.checked)}
                    className="cursor-pointer accent-amber-500"
                    data-testid="prep-preview-stale-ack"
                  />
                  I&apos;ve reviewed the stale-row conflicts and want to proceed.
                </label>
              </div>
            )}

            {validation.issues.length > 0 && (
              <div className="bg-red-500/5 border border-red-500/30 rounded-md p-3" data-testid="preview-issues">
                <p className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {validation.issues.length} issue(s) — these rows will NOT be imported
                </p>
                <ul className="text-xs text-zinc-300 max-h-40 overflow-auto space-y-1">
                  {validation.issues.slice(0, 30).map((i, idx) => (
                    <li key={idx}>Row {i.rowIndex + 2}: <span className="text-red-300">{i.type}</span> — {i.message}</li>
                  ))}
                  {validation.issues.length > 30 && <li className="text-zinc-500">… and {validation.issues.length - 30} more</li>}
                </ul>
              </div>
            )}

            <div className="bg-[#09090b] border border-[#27272a] rounded-md max-h-72 overflow-auto" data-testid="preview-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#18181b]">
                  <tr className="text-left text-zinc-400">
                    <th className="px-2 py-2">Prep</th><th className="px-2 py-2">Yield</th>
                    <th className="px-2 py-2">Lines</th><th className="px-2 py-2">Ingredients (resolved)</th>
                  </tr>
                </thead>
                <tbody>
                  {validation.preps.slice(0, 50).map((p, idx) => (
                    <tr key={idx} className="border-t border-[#27272a]">
                      <td className="px-2 py-2 font-medium" data-testid={`preview-prep-${idx}`}>{p.prep_name}</td>
                      <td className="px-2 py-2 font-mono">{p.expected_yield} {p.yield_unit}</td>
                      <td className="px-2 py-2 font-mono">{p.lines.length}</td>
                      <td className="px-2 py-2 text-zinc-400 truncate max-w-xs">
                        {p.lines.map((l) => `${l.ingredient_name} ${l.qty}${l.base_unit}`).join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setStep('mapping')} className="px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-md text-sm">Back</button>
              <button
                onClick={runImport}
                disabled={!canImportAnyway || importing}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold disabled:opacity-40"
                data-testid="preview-import-btn"
                title={!canProceed ? 'Some rows have issues and will be skipped' : ''}
              >
                {canProceed ? `Import ${validation.preps.length} prep(s)` : `Import ${validation.preps.length} (skip ${validation.issues.length} bad rows)`}
              </button>
            </div>
          </div>
        )}

        {step === 'importing' && (
          <div className="space-y-4 py-4" data-testid="import-progress">
            {importing ? (
              <p className="text-zinc-400">Importing… please wait.</p>
            ) : importResults && (
              <div className="space-y-2">
                <p className="text-emerald-400 font-bold">Created {importResults.created.length} prep(s)</p>
                {importResults.failed.length > 0 && (
                  <>
                    <p className="text-red-400 font-bold">Failed {importResults.failed.length}:</p>
                    <ul className="text-xs text-zinc-300 space-y-1">
                      {importResults.failed.map((f, i) => <li key={i}>• {f.name} — {f.error}</li>)}
                    </ul>
                  </>
                )}
                <div className="flex justify-end pt-2">
                  <button onClick={closeAndReset} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-bold">Close</button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {exitGuard.ConfirmDialog}
    </Dialog>
  );
};

export default PrepUploadDialog;
